// web/src/components/CoverLineOverrideEditor.tsx
//
// Quiet "edit cover line" affordance, sits just below the hero on
// MatchDetail. Added 2026-05-26 after a Nova hallucination landed on
// a production match (1-goal-win → "LIAMBRACE SCORES LATE WINNER")
// and we needed parents to be able to take the wheel.
//
// Design intent:
//   - Collapsed state: a single tiny eyebrow + pencil button. Doesn't
//     compete with the cover hero above it.
//   - Tap → expands into an inline editor with a 60-char input + Save
//     and Cancel. If an override is already set, also shows a "Reset
//     to AI" pill that clears the field.
//   - Save calls PATCH /match with `coverLineOverride: text` (or `null`
//     for clear). Optimistic on the parent component's state via the
//     onSaved callback.
//
// Why inline, not a modal: the cover hero IS the headline. Editing it
// is a quick "say something better" act, not a multi-step form. Modal
// felt heavy for what amounts to typing 4-5 words.

import { useState, type ReactElement } from 'react';
import type { Match } from '../types';
import { useMatchApi } from '../api/match';
import { ApiError } from '../api/client';

interface Props {
  kidId: string;
  matchId: string;
  /** What's currently being rendered as the cover line. */
  currentText: string;
  /** Truthy if the parent has set an override already. */
  currentOverride?: string;
  /** Called with the freshly-PATCHed Match on success. */
  onSaved: (m: Match) => void;
}

const MAX_LENGTH = 60;

export function CoverLineOverrideEditor({
  kidId,
  matchId,
  currentText,
  currentOverride,
  onSaved,
}: Props): ReactElement {
  const api = useMatchApi();
  const [editing, setEditing] = useState(false);
  // When the user opens the editor, prefill with the current visible
  // text. Easier to "fix one word" than to type from scratch.
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEditing = () => {
    setDraft(currentOverride ?? currentText);
    setEditing(true);
    setError(null);
  };

  const cancel = () => {
    setEditing(false);
    setDraft('');
    setError(null);
  };

  const save = async (clear = false) => {
    setSaving(true);
    setError(null);
    try {
      const trimmed = draft.trim();
      const res = await api.update(kidId, matchId, {
        coverLineOverride: clear ? null : trimmed,
      });
      onSaved(res.match);
      setEditing(false);
      setDraft('');
    } catch (err) {
      const e = err as ApiError;
      setError(e.message || 'Could not save. Try again?');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 'var(--space-base)',
          fontSize: 'var(--micro)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {currentOverride ? 'Your cover line' : 'Cover line'}
        </span>
        <span aria-hidden style={{ opacity: 0.6 }}>·</span>
        <button
          type="button"
          onClick={startEditing}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--green)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            letterSpacing: 'inherit',
            textTransform: 'inherit',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
            padding: 0,
            fontWeight: 600,
          }}
        >
          ✏️ Edit
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 'var(--space-base)',
        padding: 'var(--space-base)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--gold)',
        borderRadius: 8,
      }}
    >
      <div
        style={{
          fontSize: 'var(--micro)',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--gold)',
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Your cover line
      </div>
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, MAX_LENGTH))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void save(false);
          } else if (e.key === 'Escape') {
            cancel();
          }
        }}
        placeholder="A magazine-cover headline, in your words"
        disabled={saving}
        maxLength={MAX_LENGTH}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: 16,
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          borderRadius: 6,
          border: '1px solid var(--divider)',
          background: 'var(--bg)',
          color: 'var(--ink)',
          boxSizing: 'border-box',
          marginBottom: 6,
        }}
      />
      <div
        style={{
          fontSize: 'var(--micro)',
          color: 'var(--muted)',
          marginBottom: 12,
        }}
      >
        {draft.length}/{MAX_LENGTH} · Press Enter to save, Esc to cancel
      </div>

      {error ? (
        <p style={{ color: 'var(--error)', fontSize: 13, margin: '0 0 10px' }}>{error}</p>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => save(false)}
          disabled={saving || draft.trim().length === 0}
          style={{
            padding: '8px 16px',
            minHeight: 36,
            borderRadius: 6,
            border: 'none',
            background: 'var(--green)',
            color: 'var(--ink-on-green)',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            opacity: saving || draft.trim().length === 0 ? 0.6 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          style={{
            padding: '8px 16px',
            minHeight: 36,
            borderRadius: 6,
            border: '1px solid var(--divider)',
            background: 'transparent',
            color: 'var(--ink)',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        {currentOverride ? (
          <button
            type="button"
            onClick={() => void save(true)}
            disabled={saving}
            style={{
              padding: '6px 12px',
              background: 'transparent',
              border: 'none',
              color: 'var(--muted)',
              fontSize: 12,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              marginLeft: 'auto',
            }}
          >
            Reset to auto
          </button>
        ) : null}
      </div>
    </div>
  );
}
